import { useTranslation } from 'next-i18next';
import { BadgePill } from '@components/common';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { ItemPrice } from './ItemPrice';

interface ItemOptionProps {
  itemData?: ApiSchema<'ActivationBundle'>;
  className?: string;
}

/**
 * @description Item option component for job activation packages.
 * - `itemData`: The item data.
 * - `hasActiveSubscription`: If the user has an active subscription.
 * - `className`: Add extra classes to component.
 */
export const ItemOption = (props: ItemOptionProps) => {
  // Destructure props
  const {
    itemData,
    className,
  } = props;

  const {
    title = '',
    subtitle = '',
    credits = 0,
    upgradeCredits = 0,
    available,
    recommended,
    current,
    unavailableReason,
    bundlePriceType,
    discountedCredits,
    identifier,
  } = itemData || {};

  // Translation
  const { t } = useTranslation('common');

  // Render component
  // *************************
  return (
    <div className={`flex grow items-center ${className || ''}`}>
      <span className="grow">
        {/* Title */}
        <span className={`flex items-center text-base font-bold ${available ? 'text-ink' : 'text-ink-light'}`}>
          <span className="mr-1">{`${t(title)} •`}</span>
          <div className="flex items-center">
            <span {...bundlePriceType === 'flex' ? '' : { className: 'mr-1' }}>
              <ItemPrice
                price={credits}
                discountedCredits={discountedCredits}
                upgradePrice={upgradeCredits}
              />
            </span>
            <span className="text-sm">
              {identifier?.split('-')?.pop() === 'flex' ? `% ${t('price.percentage.from.salary')}` : t('job.add.activation.services.currency')}
            </span>
          </div>

          {/* Active package badge */}
          {current && (
            <BadgePill
              color="secondary"
              size="xxs"
              className="ml-2 shrink-0 !px-2.5 !py-0.5 font-medium uppercase"
            >
              {t('global.status.active')}
            </BadgePill>
          )}
        </span>

        {/* Subtitle */}
        <span className={available ? 'text-ink-medium' : 'text-ink-light'}>
          {subtitle}
          {unavailableReason && (
            <span className="mt-2 flex items-start gap-1.5 border-t border-dashed pt-1.5">
              <ExclamationCircleIcon
                className="mt-px size-4.5 shrink-0 text-warning"
              />
              {unavailableReason}
            </span>
          )}
        </span>
      </span>

      {/* Recommended badge */}
      {recommended && (
        <BadgePill
          color="ink-medium"
          size="xxs"
          className="shrink-0 !px-2.5 !py-0.5 uppercase"
        >
          {t('global.label.recommended')}
        </BadgePill>
      )}
    </div>
  );
};
